import { FC, useMemo } from "react";

type Props = {
  max: number;
  value: number;
  onChange: (value: number) => void;
  increment?: number; // fixed step size the slider snaps to (default 10)
  min?: number; // lower bound (default 0); snapping is offset from min
  disabled?: boolean;
  variant?: "primary" | "danger"; // "danger" renders red — used for the subtractive Point Exchange
};

/**
 * A quantized slider that snaps to fixed increments (default 10) between `min` (default 0) and `max`.
 * Used by associates to add challenge points (max = challenge.reward) and to draw down a team's balance
 * during Point Exchange. A non-zero `min` (e.g. the team's sub-increment remainder) keeps every snapped
 * value a clean multiple of `increment` away from `max`. The big readout is unambiguous at a booth.
 */
export const QuantizedSlider: FC<Props> = ({
  max,
  value,
  onChange,
  increment = 10,
  min = 0,
  disabled,
  variant = "primary",
}) => {
  const step = useMemo(() => Math.max(1, Math.min(increment, max - min || increment)), [increment, max, min]);

  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const snap = (n: number) => clamp(Math.round((n - min) / step) * step + min);

  const isDanger = variant === "danger";

  return (
    <div className="flex w-full max-w-sm flex-col items-center">
      <div className={`mb-4 text-5xl font-bold tabular-nums ${isDanger ? "text-error" : ""}`}>{value}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(snap(parseInt(e.target.value, 10)))}
        className={`range w-full ${isDanger ? "range-error" : "range-primary"}`}
      />
      <div className="mt-1 flex w-full justify-between text-xs text-gray-400">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
};
